// src/admin/pages/import-orders/ImportOrderDetailPage.tsx
//
// Replaces ImportOrderViewDialog.tsx (a MUI Drawer) with a full page reached
// at /admin/import-order/:id. Fetches its own data via useImportOrder(id)
// since — unlike the old drawer, which received the order as a prop from an
// already-fetched list row — a page can be reached directly by URL/refresh/
// bookmark. Status changes refetch and stay on the page rather than closing
// a drawer; only the explicit "Back to List" button navigates away.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, Grid, Box, Typography, IconButton, Stack, Chip, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Divider, TextField,
  FormControl, Select, MenuItem, Alert, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Cancel as CancelIcon,
  AttachMoney as AttachMoneyIcon,
  Warehouse as WarehouseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  ImportOrder, ImportOrderStatus, IMPORT_ORDER_STATUS_COLORS, PAYMENT_STATUS_COLORS,
  ImportOrderItem, LcStatus, ImportOrderDocumentType, computeCostAmount,
} from './ImportOrder';
import { ImportOrderService } from './importOrderService';
import { ImportOrderReturnService } from './importOrderReturnService';
import { WarehouseService } from '../branch/branchService';
import { Warehouse } from '../../../enums/product';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { useImportOrder } from '../../../hooks/useImportOrders';
import POReturnDialog from './ImportOrderReturnDialog';
import ToastNotification from '../common/Toastnotification';

interface WarehouseSplit {
  warehouseId: string;
  quantity: number;
}
type ItemWarehouseAllocations = { [itemId: string]: WarehouseSplit[] };

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: React.ReactNode;
  loading: boolean;
  onConfirm: () => Promise<void>;
}
const CONFIRM_DIALOG_CLOSED: ConfirmDialogState = { open: false, title: '', message: '', loading: false, onConfirm: async () => {} };

interface AlertDialogState {
  open: boolean;
  title: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}
const ALERT_DIALOG_CLOSED: AlertDialogState = { open: false, title: '', message: '', severity: 'error' };

const DOCUMENT_TYPES: ImportOrderDocumentType[] = ['LC Copy', 'Invoice', 'Bill of Lading', 'Customs Declaration', 'Other'];
const LC_STATUSES: LcStatus[] = ['Not Opened', 'Opened', 'Closed'];

const formatAmount = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };
  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };
  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };
  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;
  return {
    backgroundColor: p.bg, borderColor: p.border, color: p.text, fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor }, '& .MuiChip-label': { color: p.text },
  };
};

const poStatusToSoftColor = (status: ImportOrderStatus): string => {
  const raw = IMPORT_ORDER_STATUS_COLORS[status];
  const map: Record<string, string> = { default: 'default', warning: 'warning', info: 'info', primary: 'primary', success: 'success', error: 'error', secondary: 'secondary' };
  return map[raw] ?? 'default';
};
const paymentStatusToSoftColor = (status: string): string => {
  const raw = (PAYMENT_STATUS_COLORS as Record<string, string>)[status] ?? 'default';
  const map: Record<string, string> = { default: 'default', warning: 'warning', info: 'info', primary: 'primary', success: 'success', error: 'error', secondary: 'secondary' };
  return map[raw] ?? 'default';
};

const ImportOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { data: po, isLoading: poLoading, refetch } = useImportOrder(id);

  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'info' });
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => setToast({ open: true, message, severity });

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [showWarehouseAllocation, setShowWarehouseAllocation] = useState(false);
  const [itemWarehouseAllocations, setItemWarehouseAllocations] = useState<ItemWarehouseAllocations>({});
  const [receivingLoading, setReceivingLoading] = useState(false);
  const [receivingError, setReceivingError] = useState('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [poReturns, setPoReturns] = useState<any[]>([]);

  // ── Letter of Credit inline edit ──
  const [lcNumber, setLcNumber] = useState('');
  const [lcStatus, setLcStatus] = useState<LcStatus>('Not Opened');
  const [lcOpenedDate, setLcOpenedDate] = useState('');
  const [lcSaving, setLcSaving] = useState(false);
  const [lcEditing, setLcEditing] = useState(false);
  const lcInitialized = useRef(false);

  // ── Documents ──
  const [docUploading, setDocUploading] = useState(false);
  const [pendingDocType, setPendingDocType] = useState<ImportOrderDocumentType>('Other');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSubmit = isSuperAdmin || hasPermission('import_order.submit');
  const canApprove = isSuperAdmin || hasPermission('import_order.approve');
  const canOrder = isSuperAdmin || hasPermission('import_order.order');
  const canReceive = isSuperAdmin || hasPermission('import_order.receive');
  const canPayment = isSuperAdmin || hasPermission('import_order.payment');
  const canReturn = isSuperAdmin || hasPermission('inventory_return.manage');
  const canEdit = isSuperAdmin || hasPermission('import_order.edit');

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(CONFIRM_DIALOG_CLOSED);
  const openConfirmDialog = (title: string, message: React.ReactNode, onConfirm: () => Promise<void>) =>
    setConfirmDialog({ open: true, title, message, loading: false, onConfirm });
  const handleConfirmDialogConfirm = async () => {
    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try { await confirmDialog.onConfirm(); } finally { setConfirmDialog(CONFIRM_DIALOG_CLOSED); }
  };
  const handleConfirmDialogCancel = () => { if (!confirmDialog.loading) setConfirmDialog(CONFIRM_DIALOG_CLOSED); };

  const [alertDialog, setAlertDialog] = useState<AlertDialogState>(ALERT_DIALOG_CLOSED);
  const openAlertDialog = (title: string, message: string, severity: 'error' | 'warning' | 'info' = 'error') =>
    setAlertDialog({ open: true, title, message, severity });
  const handleAlertDialogClose = () => setAlertDialog(ALERT_DIALOG_CLOSED);

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  useEffect(() => {
    if (!po) return;
    if (po.status === ImportOrderStatus.ORDERED) fetchWarehouses();
    if (po.status === ImportOrderStatus.RECEIVED) fetchPOReturns();
    if (!lcInitialized.current) {
      lcInitialized.current = true;
      setLcNumber(po.lcNumber || '');
      setLcStatus(po.lcStatus || 'Not Opened');
      setLcOpenedDate(po.lcOpenedDate ? new Date(po.lcOpenedDate).toISOString().split('T')[0] : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po?.id, po?.status]);

  const fetchPOReturns = async () => {
    try {
      const returns = await ImportOrderReturnService.getReturnsForImportOrder(po!.id!);
      setPoReturns(returns);
    } catch (err) { console.error(err); }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await WarehouseService.getActiveWarehouses();
      setWarehouses(data);
      const defaultAllocations: ItemWarehouseAllocations = {};
      po!.items.forEach(item => {
        if (item.id && item.acquisitionType === 'brand_new_batch') {
          defaultAllocations[item.id] = [{ warehouseId: data.length === 1 ? data[0].id! : '', quantity: item.quantity }];
        }
      });
      setItemWarehouseAllocations(defaultAllocations);
    } catch (err) { console.error(err); }
  };

  const addWarehouseSplit = (itemId: string) =>
    setItemWarehouseAllocations(prev => ({ ...prev, [itemId]: [...(prev[itemId] || []), { warehouseId: '', quantity: 0 }] }));
  const removeWarehouseSplit = (itemId: string, splitIndex: number) =>
    setItemWarehouseAllocations(prev => {
      const splits = [...(prev[itemId] || [])];
      splits.splice(splitIndex, 1);
      return { ...prev, [itemId]: splits };
    });
  const updateSplitWarehouse = (itemId: string, splitIndex: number, warehouseId: string) =>
    setItemWarehouseAllocations(prev => {
      const splits = [...(prev[itemId] || [])];
      splits[splitIndex] = { ...splits[splitIndex], warehouseId };
      return { ...prev, [itemId]: splits };
    });
  const updateSplitQuantity = (itemId: string, splitIndex: number, quantity: number) =>
    setItemWarehouseAllocations(prev => {
      const splits = [...(prev[itemId] || [])];
      splits[splitIndex] = { ...splits[splitIndex], quantity };
      return { ...prev, [itemId]: splits };
    });

  const allocatedQuantity = (itemId: string): number =>
    (itemWarehouseAllocations[itemId] || []).reduce((sum, s) => sum + (s.quantity || 0), 0);

  const itemLabel = (item: ImportOrderItem): string =>
    `${item.brandName} ${item.modelName}${item.trimName ? ' ' + item.trimName : ''}`.trim();

  const handleStatusChange = async (status: ImportOrderStatus) => {
    if (status === ImportOrderStatus.RECEIVED) {
      setShowWarehouseAllocation(true);
      setReceivingError('');
      return;
    }
    openConfirmDialog(
      'Update Order Status',
      <>Are you sure you want to change the status to <strong>"{status}"</strong>?</>,
      async () => {
        if (!currentUser) return;
        try {
          setStatusUpdateLoading(true);
          await ImportOrderService.updateImportOrderStatus(
            po!.id!, status, currentUser.uid, currentUser.displayName || currentUser.email || 'Unknown'
          );
          await refetch();
          showToast('Import order status updated successfully', 'success');
        } catch (err: any) {
          openAlertDialog('Status Update Failed', 'Failed to update status: ' + (err.message || 'Unknown error'));
        } finally {
          setStatusUpdateLoading(false);
        }
      }
    );
  };

  const handleReceiveWithWarehouseAllocation = async () => {
    if (!currentUser || !po) return;
    const batchItems = po.items.filter(item => item.acquisitionType === 'brand_new_batch');
    let validationError = '';
    for (const item of batchItems) {
      if (!item.id) continue;
      const splits = itemWarehouseAllocations[item.id] || [];
      if (splits.length === 0) { validationError = `Please add at least one show room allocation for "${itemLabel(item)}".`; break; }
      for (const split of splits) {
        if (!split.warehouseId) { validationError = `Please select a show room for all rows of "${itemLabel(item)}".`; break; }
        if (!split.quantity || split.quantity <= 0) { validationError = `Please enter a valid quantity for all rows of "${itemLabel(item)}".`; break; }
      }
      if (validationError) break;
      const totalAllocated = allocatedQuantity(item.id);
      if (totalAllocated !== item.quantity) {
        validationError = `Total allocated quantity for "${itemLabel(item)}" (${totalAllocated}) must equal ordered quantity (${item.quantity}).`;
        break;
      }
    }
    if (validationError) { setReceivingError(validationError); return; }
    try {
      setReceivingLoading(true);
      setReceivingError('');
      const expandedItems: ImportOrderItem[] = [];
      for (const item of po.items) {
        if (item.acquisitionType === 'used_individual') {
          expandedItems.push(item);
          continue;
        }
        const splits = item.id ? itemWarehouseAllocations[item.id] || [] : [];
        for (const split of splits) {
          expandedItems.push({
            ...item,
            quantity: split.quantity,
            totalPrice: item.unitPrice * split.quantity,
            receivedQuantity: split.quantity,
            warehouseId: split.warehouseId,
            warehouseName: warehouses.find(w => w.id === split.warehouseId)?.name,
          });
        }
      }
      await ImportOrderService.markImportOrderAsReceived(
        po.id!, expandedItems, currentUser.uid, currentUser.displayName || currentUser.email || 'Unknown'
      );
      setShowWarehouseAllocation(false);
      await refetch();
      showToast('Import order received and stock updated', 'success');
    } catch (err: any) {
      setReceivingError(err.message || 'Failed to receive import order');
    } finally {
      setReceivingLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!po || !currentUser) return;
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) { openAlertDialog('Invalid Payment Amount', 'Please enter a valid payment amount.', 'warning'); return; }
    if (po.paidAmount + amount > po.totalAmount) { openAlertDialog('Payment Exceeds Total', 'Payment amount exceeds the total order amount.', 'warning'); return; }
    try {
      setPaymentLoading(true);
      await ImportOrderService.recordPayment(po.id!, amount, currentUser.uid, currentUser.displayName || currentUser.email || 'Unknown');
      setPaymentAmount('');
      await refetch();
      showToast('Payment recorded', 'success');
    } catch (err: any) {
      openAlertDialog('Payment Failed', 'Failed to record payment: ' + (err.message || 'Unknown error'));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleSaveLc = async () => {
    if (!po) return;
    try {
      setLcSaving(true);
      await ImportOrderService.updateLcDetails(po.id!, lcNumber, lcStatus, lcOpenedDate ? new Date(lcOpenedDate) : null);
      await refetch();
      showToast('LC details updated', 'success');
      setLcEditing(false);
    } catch (err: any) {
      showToast(`Failed to update LC details: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setLcSaving(false);
    }
  };

  const handleCancelLcEdit = () => {
    setLcNumber(po?.lcNumber || '');
    setLcStatus(po?.lcStatus || 'Not Opened');
    setLcOpenedDate(po?.lcOpenedDate ? new Date(po.lcOpenedDate).toISOString().split('T')[0] : '');
    setLcEditing(false);
  };

  // Firebase Storage download URLs are cross-origin from this app, so a plain
  // `<a download>` gets silently ignored by the browser — fetch the file as a
  // blob and trigger the save from an object URL instead.
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const handleDownloadDocument = async (docUrl: string, docName: string, docId: string) => {
    setDownloadingDocId(docId);
    try {
      const res = await fetch(docUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = docName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      showToast('Failed to download document', 'error');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleUploadDocuments = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!po) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    try {
      setDocUploading(true);
      await ImportOrderService.uploadImportOrderDocuments(
        po.id!, files.map(file => ({ file, type: pendingDocType }))
      );
      await refetch();
      showToast('Document(s) uploaded', 'success');
    } catch (err: any) {
      showToast(`Failed to upload document(s): ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDocUploading(false);
      e.target.value = '';
    }
  };

  if (poLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={8}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading order…</Typography>
      </Box>
    );
  }

  if (!po) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 700, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>Import order not found.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/import-order')}>Back to List</Button>
      </Box>
    );
  }

  const outstandingAmount = po.totalAmount - po.paidAmount;

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header — fixed, does not scroll. Same fixed-header/scrollable-middle/
          fixed-footer pattern as ImportOrderFormPage. */}
      <Box sx={{ flexShrink: 0, px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap">
          <IconButton onClick={() => navigate('/admin/import-order')} aria-label="Back to list">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Import Order — {po.poNumber}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip label={`Status: ${po.status}`} size="small" variant="outlined" sx={getSoftChipSx(poStatusToSoftColor(po.status), isDark)} />
            <Chip label={`Payment: ${po.paymentStatus}`} size="small" variant="outlined" sx={getSoftChipSx(paymentStatusToSoftColor(po.paymentStatus), isDark)} />
            {po.stockUpdated && <Chip label="Stock Updated" size="small" variant="outlined" icon={<CheckCircleIcon />} sx={getSoftChipSx('success', isDark)} />}
          </Stack>
        </Stack>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <Box sx={{ px: { xs: 2, md: 3 }, pb: 2, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      <Grid container spacing={2}>
        {/* Supplier Info */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Supplier Information</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>{po.supplierName}</Typography>
            <Typography variant="body2" color="text.secondary">Email: {po.supplierEmail}</Typography>
            <Typography variant="body2" color="text.secondary">Phone: {po.supplierPhone}</Typography>
          </Paper>
        </Grid>

        {/* Order Info */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Order Information</Typography>
            <Typography variant="body2">Created: {po.createdAt.toLocaleDateString()} by {po.createdByName}</Typography>
            {po.expectedDeliveryDate && <Typography variant="body2" color="text.secondary">Expected Delivery: {po.expectedDeliveryDate.toLocaleDateString()}</Typography>}
            {po.actualDeliveryDate && <Typography variant="body2">Delivered: {po.actualDeliveryDate.toLocaleDateString()}</Typography>}
            {po.approvedByName && <Typography variant="body2">Approved by: {po.approvedByName}</Typography>}
            {po.receivedByName && <Typography variant="body2">Received by: {po.receivedByName}</Typography>}
          </Paper>
        </Grid>

        {/* Status History — who changed each status, and when. */}
        {(po.statusHistory && po.statusHistory.length > 0) && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <HistoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">Status History</Typography>
              </Stack>
              <Stack spacing={1}>
                {po.statusHistory.map((h, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={0.5}>
                    <Chip label={h.status} size="small" variant="outlined" sx={getSoftChipSx(poStatusToSoftColor(h.status), isDark)} />
                    <Typography variant="body2" color="text.secondary">
                      by {h.changedByName}{h.changedAt ? ` · ${new Date(h.changedAt).toLocaleString()}` : ''}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Items Table */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Order Items</Typography>
          <Paper variant="outlined" sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>VIN</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Received</TableCell>
                  {po.status === ImportOrderStatus.RECEIVED && <TableCell>Show Room</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {po.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{itemLabel(item)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {item.acquisitionType === 'used_individual' ? 'Used' : 'Brand New'}{item.vin ? ` · ${item.vin}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip label={item.acquisitionType === 'used_individual' ? 'Used' : 'Brand New'} size="small" variant="outlined"
                        sx={getSoftChipSx(item.acquisitionType === 'used_individual' ? 'info' : 'success', isDark)} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{item.vin || '-'}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{currencySymbol} {formatAmount(item.unitPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{currencySymbol} {formatAmount(item.totalPrice)}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${item.receivedQuantity}/${item.quantity}`} size="small" variant="outlined"
                        sx={getSoftChipSx(item.receivedQuantity === item.quantity ? 'success' : 'default', isDark)} />
                    </TableCell>
                    {po.status === ImportOrderStatus.RECEIVED && (
                      <TableCell>
                        {item.warehouseName ? (
                          <Chip label={item.warehouseName} size="small" variant="outlined" icon={<WarehouseIcon />} sx={getSoftChipSx('primary', isDark)} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">Not assigned</Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Warehouse Allocation */}
        {showWarehouseAllocation && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <WarehouseIcon color="primary" />
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Assign Show Rooms for Received Items</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Assign one or more show rooms per brand-new batch item. Total allocated quantity must equal
                ordered quantity. Used/individual items already have their show room assigned and aren't shown here.
              </Typography>
              {receivingError && <Alert severity="error" sx={{ mb: 2 }}>{receivingError}</Alert>}

              {po.items.filter(item => item.acquisitionType === 'brand_new_batch').map((item, itemIndex) => {
                const itemId = item.id!;
                const splits = itemWarehouseAllocations[itemId] || [];
                const totalAllocated = allocatedQuantity(itemId);
                const remaining = item.quantity - totalAllocated;
                const isBalanced = remaining === 0;

                return (
                  <Paper key={itemIndex} variant="outlined" sx={{ p: 2, mb: 2, borderColor: isBalanced ? 'success.main' : 'warning.main', borderWidth: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1.5 }} spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{itemLabel(item)}</Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                        <Chip label={`Total: ${item.quantity}`} size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} />
                        <Chip label={`Allocated: ${totalAllocated}`} size="small" variant="outlined" sx={getSoftChipSx(isBalanced ? 'success' : 'warning', isDark)} />
                        {!isBalanced && <Chip label={`Remaining: ${remaining}`} size="small" variant="outlined" sx={getSoftChipSx('error', isDark)} />}
                      </Stack>
                    </Stack>

                    <Table size="small" sx={{ mb: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '55%' }}>Show Room</TableCell>
                          <TableCell sx={{ width: '30%' }}>Quantity</TableCell>
                          <TableCell sx={{ width: '15%' }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {splits.map((split, splitIndex) => (
                          <TableRow key={splitIndex}>
                            <TableCell>
                              <FormControl fullWidth size="small">
                                <Select value={split.warehouseId} onChange={e => updateSplitWarehouse(itemId, splitIndex, e.target.value)} displayEmpty>
                                  <MenuItem value="" disabled>Select show room</MenuItem>
                                  {warehouses.map(warehouse => (
                                    <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name} ({warehouse.code})</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField size="small" type="number" value={split.quantity}
                                onChange={e => updateSplitQuantity(itemId, splitIndex, parseInt(e.target.value, 10) || 0)}
                                inputProps={{ min: 1, max: item.quantity }} fullWidth />
                            </TableCell>
                            <TableCell align="center">
                              {splits.length > 1 && (
                                <IconButton size="small" color="error" onClick={() => removeWarehouseSplit(itemId, splitIndex)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Button size="small" startIcon={<AddIcon />} onClick={() => addWarehouseSplit(itemId)} disabled={isBalanced} variant="outlined" color="primary" sx={{ mt: 0.5 }}>
                      Add Show Room
                    </Button>
                  </Paper>
                );
              })}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} justifyContent="flex-end">
                <Button onClick={() => { setShowWarehouseAllocation(false); setReceivingError(''); }} disabled={receivingLoading}>Cancel</Button>
                <Button onClick={handleReceiveWithWarehouseAllocation} variant="contained" color="success"
                  startIcon={receivingLoading ? <CircularProgress size={16} /> : <LocalShippingIcon />} disabled={receivingLoading}>
                  {receivingLoading ? 'Receiving...' : 'Confirm Receipt & Update Stock'}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Financial Summary */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Financial Summary</Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Items Subtotal:</Typography>
                <Typography variant="body2">{currencySymbol} {formatAmount(po.subtotal)}</Typography>
              </Stack>

              {/* Current model: a flexible costs list */}
              {(po.costs || []).map(c => (
                <Stack key={c.id} direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {c.name}{c.amountType === 'percentage' ? ` (${c.value}%)` : ''}:
                  </Typography>
                  <Typography variant="body2">{currencySymbol} {formatAmount(computeCostAmount(c, po.subtotal))}</Typography>
                </Stack>
              ))}

              {/* Legacy orders (created before the cost rework) — fields
                  still on the stored doc but not part of `costs`. */}
              {(!po.costs || po.costs.length === 0) && (
                <>
                  {!!po.taxRate && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Tax ({po.taxRate}%):</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.taxAmount || 0)}</Typography>
                    </Stack>
                  )}
                  {!!po.shippingCost && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Shipping:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.shippingCost)}</Typography>
                    </Stack>
                  )}
                  {!!po.cifValue && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">CIF Value (reference only):</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.cifValue)}</Typography>
                    </Stack>
                  )}
                  {!!po.customsDuty && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Customs Duty:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.customsDuty)}</Typography>
                    </Stack>
                  )}
                  {!!po.vatAmount && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">VAT:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.vatAmount)}</Typography>
                    </Stack>
                  )}
                  {!!po.luxuryTax && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Luxury Tax:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(po.luxuryTax)}</Typography>
                    </Stack>
                  )}
                  {(po.otherTaxes || []).map(t => (
                    <Stack key={t.id} direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t.name}:</Typography>
                      <Typography variant="body2">{currencySymbol} {formatAmount(t.amount)}</Typography>
                    </Stack>
                  ))}
                </>
              )}

              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Total:</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{currencySymbol} {formatAmount(po.totalAmount)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Paid:</Typography>
                <Typography variant="body2">{currencySymbol} {formatAmount(po.paidAmount)}</Typography>
              </Stack>
              {outstandingAmount > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Outstanding:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{currencySymbol} {formatAmount(outstandingAmount)}</Typography>
                </Stack>
              )}

              {(po.payments && po.payments.length > 0) && (
                <>
                  <Divider sx={{ mt: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Payment History</Typography>
                  {po.payments.map(p => (
                    <Stack key={p.id} direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        {p.paidAt ? new Date(p.paidAt).toLocaleDateString() : ''} · {p.recordedByName}
                      </Typography>
                      <Typography variant="caption">{currencySymbol} {formatAmount(p.amount)}</Typography>
                    </Stack>
                  ))}
                </>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Record Payment */}
        {outstandingAmount > 0 && canPayment && (
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Record Payment</Typography>
              <Stack direction="row" spacing={1}>
                <TextField size="small" type="number" label="Payment Amount" value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  inputProps={{ min: 0, max: outstandingAmount, step: 0.01 }} fullWidth />
                <Button variant="contained" onClick={handlePayment} disabled={paymentLoading || !paymentAmount} startIcon={<AttachMoneyIcon />}>
                  Record
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Maximum: {currencySymbol} {formatAmount(outstandingAmount)}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Letter of Credit — shown regardless of order status, since LC
            status typically changes after the order has left DRAFT. Edited
            behind an explicit Edit button rather than always-open fields, so
            it matches the read-then-edit pattern used elsewhere on this page. */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Letter of Credit</Typography>
              {canEdit && !lcEditing && (
                <IconButton size="small" onClick={() => setLcEditing(true)} aria-label="Edit LC details">
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            {canEdit && lcEditing ? (
              <Stack spacing={1.5}>
                <TextField size="small" label="LC Number" value={lcNumber} onChange={(e) => setLcNumber(e.target.value)} disabled={lcSaving} />
                <TextField size="small" select label="LC Status" value={lcStatus} onChange={(e) => setLcStatus(e.target.value as LcStatus)} disabled={lcSaving}>
                  {LC_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <TextField size="small" label="LC Opened Date" type="date" value={lcOpenedDate}
                  onChange={(e) => setLcOpenedDate(e.target.value)} InputLabelProps={{ shrink: true }} disabled={lcSaving} />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" size="small" onClick={handleSaveLc} disabled={lcSaving}
                    startIcon={lcSaving ? <CircularProgress size={16} /> : undefined}>
                    {lcSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="small" onClick={handleCancelLcEdit} disabled={lcSaving}>Cancel</Button>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={0.5}>
                <Typography variant="body2">LC Number: {po.lcNumber || '—'}</Typography>
                <Typography variant="body2">Status: {po.lcStatus || 'Not Opened'}</Typography>
                <Typography variant="body2">Opened: {po.lcOpenedDate ? po.lcOpenedDate.toLocaleDateString() : '—'}</Typography>
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Documents — also available for the order's whole lifecycle. */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
              <Typography variant="subtitle2" color="text.secondary">Documents</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
                {(po.documents || []).length > 0 && (
                  <Button
                    size="small" variant="text" startIcon={<DownloadIcon fontSize="small" />}
                    onClick={() => (po.documents || []).forEach(doc => handleDownloadDocument(doc.url, doc.name, doc.id))}
                  >
                    Download All
                  </Button>
                )}
                {canEdit && (
                  <>
                    <TextField
                      select size="small" label="Type" value={pendingDocType}
                      onChange={(e) => setPendingDocType(e.target.value as ImportOrderDocumentType)}
                      sx={{ minWidth: 160 }} disabled={docUploading}
                    >
                      {DOCUMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                    <Button component="label" size="small" variant="outlined" startIcon={docUploading ? <CircularProgress size={14} /> : <UploadFileIcon />} disabled={docUploading}>
                      Upload
                      <input ref={fileInputRef} type="file" hidden multiple onChange={handleUploadDocuments} />
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
            {(po.documents || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No documents uploaded yet.</Typography>
            ) : (
              <Stack spacing={1}>
                {(po.documents || []).map(doc => (
                  <Stack key={doc.id} direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>{doc.name}</Typography>
                    <Chip size="small" label={doc.type} sx={getSoftChipSx('info', isDark)} />
                    <Typography variant="caption" color="text.secondary">{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ''}</Typography>
                    <Button size="small" href={doc.url} target="_blank" rel="noopener noreferrer">View</Button>
                    <Button
                      size="small" startIcon={downloadingDocId === doc.id ? <CircularProgress size={12} /> : <DownloadIcon fontSize="small" />}
                      disabled={downloadingDocId === doc.id}
                      onClick={() => handleDownloadDocument(doc.url, doc.name, doc.id)}
                    >
                      Download
                    </Button>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Notes */}
        {po.notes && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notes</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{po.notes}</Typography>
            </Paper>
          </Grid>
        )}

        {po.internalNotes && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: isDark ? 'rgba(255,167,38,0.08)' : 'rgba(255,167,38,0.06)', borderColor: isDark ? 'rgba(255,167,38,0.35)' : 'rgba(255,167,38,0.30)' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Internal Notes</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{po.internalNotes}</Typography>
            </Paper>
          </Grid>
        )}

        {/* Returns */}
        {poReturns.length > 0 && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, overflow: 'auto' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Returns for this Import Order</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Return Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Refund</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {poReturns.map((ret: any) => (
                    <TableRow key={ret.id}>
                      <TableCell>{ret.returnNumber}</TableCell>
                      <TableCell>
                        <Chip label={ret.status} size="small" variant="outlined" sx={getSoftChipSx(ret.status === 'completed' ? 'success' : 'warning', isDark)} />
                      </TableCell>
                      <TableCell align="right">{currencySymbol} {formatAmount(ret.totalAmount)}</TableCell>
                      <TableCell align="right">{ret.refundIssued ? `${currencySymbol} ${formatAmount(ret.refundAmount)}` : '-'}</TableCell>
                      <TableCell>{ret.createdAt.toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}
      </Grid>
      </Box>
      </Box>

      {/* Footer actions — fixed, does not scroll (mirrors ImportOrderFormPage). */}
      {!showWarehouseAllocation && (
        <Box sx={{ flexShrink: 0, px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
          <Divider sx={{ mb: 2 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
            {canSubmit && po.status === ImportOrderStatus.DRAFT && (
              <Button onClick={() => handleStatusChange(ImportOrderStatus.PENDING)} variant="outlined" color="warning" disabled={statusUpdateLoading} size="small">
                Submit for Approval
              </Button>
            )}
            {canApprove && po.status === ImportOrderStatus.PENDING && (
              <>
                <Button onClick={() => handleStatusChange(ImportOrderStatus.APPROVED)} variant="outlined" color="success" startIcon={<CheckCircleIcon />} disabled={statusUpdateLoading} size="small">
                  Approve
                </Button>
                <Button onClick={() => handleStatusChange(ImportOrderStatus.CANCELLED)} variant="outlined" color="error" startIcon={<CancelIcon />} disabled={statusUpdateLoading} size="small">
                  Cancel
                </Button>
              </>
            )}
            {canOrder && po.status === ImportOrderStatus.APPROVED && (
              <Button onClick={() => handleStatusChange(ImportOrderStatus.ORDERED)} variant="outlined" color="primary" disabled={statusUpdateLoading} size="small">
                Mark as Ordered
              </Button>
            )}
            {canReceive && po.status === ImportOrderStatus.ORDERED && !po.stockUpdated && (
              <Button onClick={() => handleStatusChange(ImportOrderStatus.RECEIVED)} variant="outlined" color="success" startIcon={<LocalShippingIcon />} disabled={statusUpdateLoading} size="small">
                Mark as Received
              </Button>
            )}
            {canReturn && po.status === ImportOrderStatus.RECEIVED && (
              <Button onClick={() => setReturnDialogOpen(true)} variant="outlined" color="error" startIcon={<CancelIcon />} size="small">
                Create Return
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => navigate('/admin/import-order')} variant="contained" size="small">Back to List</Button>
          </Stack>
        </Box>
      )}

      <POReturnDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        onSuccess={() => { setReturnDialogOpen(false); fetchPOReturns(); refetch(); }}
        purchaseOrder={po}
      />

      <Dialog open={confirmDialog.open} onClose={handleConfirmDialogCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent><DialogContentText component="div">{confirmDialog.message}</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleConfirmDialogCancel} disabled={confirmDialog.loading}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmDialogConfirm} disabled={confirmDialog.loading}
            startIcon={confirmDialog.loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}>
            {confirmDialog.loading ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={alertDialog.open} onClose={handleAlertDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{alertDialog.title}</DialogTitle>
        <DialogContent><Alert severity={alertDialog.severity} sx={{ mt: 1 }}>{alertDialog.message}</Alert></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleAlertDialogClose}>OK</Button>
        </DialogActions>
      </Dialog>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} />
    </Box>
  );
};

export default ImportOrderDetailPage;
